import styled from '@emotion/styled';
import React, { useEffect, useRef, useState } from 'react';
import { Slide, ToastContainer } from 'react-toastify';

import { getAppOsInfo } from '../../utils';
import { FooterContainer } from './FooterContainer';
import { HeaderContainer } from './HeaderContainer';

interface PageLayoutProps {
  hideGnbInSSR?: boolean;
  children?: React.ReactNode;
}

export const PageLayoutBase = ({
  className,
  children,
  hideGnbInSSR,
}: {
  className?: string;
  children?: React.ReactNode;
  hideGnbInSSR?: boolean;
}): React.ReactElement => {
  const [footerHeight, setFooterHeight] = useState<number>(0);
  const footerRef = useRef<HTMLDivElement>(null);
  const isAndroid = getAppOsInfo()?.osType === 'Android';

  useEffect(() => {
    const handleFooterHeight = (): void => {
      if (!footerRef.current) {
        return;
      }

      setFooterHeight(footerRef.current.offsetHeight);
    };

    if (footerRef.current) {
      setFooterHeight(footerRef.current.offsetHeight);
      window.addEventListener('resize', handleFooterHeight);
    }

    return () => {
      window.removeEventListener('resize', handleFooterHeight);
    };
  }, [footerRef]);

  return (
    <ContainerColumn>
      <HeaderContainer hideGnbInSSR={hideGnbInSSR} />
      <ContainerRow className={className} footerHeight={footerHeight}>
        {children}
      </ContainerRow>
      <FooterContainer footerRef={footerRef} />
      <ToastContainer
        position="bottom-center"
        hideProgressBar
        autoClose={2000}
        transition={Slide}
        style={{
          marginBottom: isAndroid ? '42px' : '0px',
          zIndex: 10001,
        }}
      />
    </ContainerColumn>
  );
};

export function PageLayout(props: PageLayoutProps) {
  const { children, hideGnbInSSR } = props;

  return (
    <PageLayoutBase hideGnbInSSR={hideGnbInSSR}>
      <ContentsContainer>
        <PageWrap>{children}</PageWrap>
      </ContentsContainer>
    </PageLayoutBase>
  );
}

const ContainerColumn = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  position: relative;
`;

const ContainerRow = styled.div<{ footerHeight: number }>`
  display: flex;
  width: 100%;
  padding-bottom: ${({ footerHeight }) => footerHeight}px;
`;

const ContentsContainer = styled.div`
  width: 100%;
  @media screen and (min-width: 768px) {
    padding: 0px 60px;
    margin: 0 auto;
    box-sizing: border-box;
    flex: 1 0 auto;
    max-width: 1256px;
  }
`;

const PageWrap = styled.div`
  width: 100%;
`;
